import React from "react";
import { Sparkles, SearchCheck, Mic, Share2, Star, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

const pillars = [
  {
    n: "01",
    title: "A.I. Search Findable",
    desc: "Engineered for the new age of A.I.-driven discovery — your website is indexed and surfaced by ChatGPT, Gemini, Perplexity, Copilot and every emerging A.I. answer engine.",
    icon: Sparkles,
    accent: "from-cyan-500 to-cyan-700",
  },
  {
    n: "02",
    title: "Google & Directories S.E.O.",
    desc: "Search Engine Optimized for Google, Bing and the directory networks that customers still rely on — built around proven ranking signals, structured data and authority links.",
    icon: SearchCheck,
    accent: "from-sky-500 to-sky-700",
  },
  {
    n: "03",
    title: "Voice & Ask Engine Optimized",
    desc: "Tuned for the way real people actually ask — Alexa, Siri, Bixby, Cortana and Google Assistant — using A.E.O. structure so spoken questions return your business.",
    icon: Mic,
    accent: "from-amber-500 to-amber-600",
  },
  {
    n: "04",
    title: "Social Media Integrated",
    desc: "Facebook, Instagram, LinkedIn, X, YouTube and TikTok plug directly into your site — feeds, share buttons, pixels and re-targeting work as one connected ecosystem.",
    icon: Share2,
    accent: "from-rose-500 to-rose-600",
  },
  {
    n: "05",
    title: "Google Reviews Integrated",
    desc: "Live 5-star Google reviews are pulled into your site automatically — building instant trust, lifting conversion rates and feeding Google's local trust signals.",
    icon: Star,
    accent: "from-emerald-500 to-emerald-700",
  },
];

export default function Pillars() {
  return (
    <section className="relative py-20 lg:py-28 bg-white overflow-hidden">
      {/* Subtle background flourish */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-[60rem] rounded-full bg-cyan-100/40 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-cyan-700 font-bold">
            The 411TechSavvy Framework
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-[1.1]">
            Every website built stands on{" "}
            <span className="text-cyan-700">five up-to-date principles.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Most websites are still designed for yesterday&rsquo;s internet. Mine are engineered
            for <strong>conversion</strong>  the way customers <em>actually</em> find businesses
            today through artificial intelligence, voice assistants, search engines, social
            feeds and live reviews. Five pillars, one connected ecosystem, designed to keep your
            business discoverable for the next decade.
          </p>
        </div>

        {/* Pillars grid */}
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillars.map((p) => {
            const Ic = p.icon;
            return (
              <article
                key={p.n}
                className="group relative rounded-2xl border border-slate-200 bg-white p-7 card-hover overflow-hidden"
              >
                <div
                  className={`absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${p.accent} opacity-10 group-hover:opacity-20 transition-opacity`}
                />
                <div className="flex items-start justify-between">
                  <div
                    className={`h-12 w-12 rounded-xl bg-gradient-to-br ${p.accent} text-white grid place-items-center shadow-md`}
                  >
                    <Ic className="h-6 w-6" />
                  </div>
                  <div className="text-4xl font-extrabold text-slate-100 group-hover:text-slate-200 tracking-tight">
                    {p.n}
                  </div>
                </div>
                <h3 className="mt-5 font-bold text-slate-900 text-lg leading-tight">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </article>
            );
          })}

          {/* CTA card filling the 6th slot on lg */}
          <article className="relative rounded-2xl bg-slate-900 text-white p-7 overflow-hidden">
            <div className="absolute -bottom-12 -right-8 h-40 w-40 rounded-full bg-cyan-500/30 blur-2xl" />
            <div className="relative">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold">
                Ready to upgrade?
              </div>
              <h3 className="mt-3 text-2xl font-extrabold leading-tight">
                Let&rsquo;s build you a website that wins on every front.
              </h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                Free consultation. No pressure  just a clear, honest assessment of where your
                site stands today and where it could be.
              </p>
              <Button
                asChild
                className="mt-6 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold h-11 px-5"
              >
                <a href="#contact">
                  Start the conversation <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
